import { mergeAttributes, Node, nodePasteRule } from "@tiptap/core";
import { ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionOptions, type SuggestionProps } from "@tiptap/suggestion";
import { type ComponentProps } from "react";

import { TemplateVariableSelectOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/TemplateVariableSelectOverlay/TemplateVariableSelectOverlay";
import { TemplateVariableNodeView } from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableNodeView";

export const TemplateVariableExtensionName = "templateVariable";
export const TemplateVariableTriggerChar = "{";

/**
 * How a chip renders when the editor is read-only (ignored while editing, which always shows chips):
 *  - `"replaceVariables"`: drop the chip entirely and render its resolved text inline, so the document
 *    reads as finished output. A chip whose source supplies no values resolves to a `{braced}`
 *    placeholder (see `resolveTemplateVariableText`);
 *  - `"renderChips"`: keep the styled chip (as in edit mode) so the reader can see it's templated.
 * Defaults to `"replaceVariables"` when unset.
 */
export type TemplateVariableReadOnlyMode = "replaceVariables" | "renderChips";

/**
 * All variables that live on the source types must be string, numbers, or undefined.
 */
type TemplateVariableValues = Record<string, string | number | undefined>;

/**
 * Represents mapping of a template class to the type expected by the template variable extension.
 *
 * Example conversion:
 * class JobInfo {
 *  jobName: string;
 *  jobAddress: string;
 *  jobId: number;
 * }
 *
 * This will enforce that the template variables have all of the properties of the job info class, and that they are all strings or numbers.
 * And it will make sure a label and optional description is provided for each property in the template variable definition.
 *
 * var template = TemplateVariableDefinition<JobInfo> = {
 *  jobName: "Job Name",
 *  jobAddress: { label: "Job Address", description: "The address of the job site" },
 *  jobId: "Job ID"
 * }
 *
 * So on the source object, jobId is a number, but this object expects a string label for it.
 *
 */
// A mapped type (must be `type`, not `interface`), so it keys off `keyof T` to enforce exact keys.
export type TemplateVariableDefinition<T extends TemplateVariableValues = TemplateVariableValues> =
    {
        [K in keyof T]: string | { label: string; description?: string };
    };

/** A single flattened definition — the array-shaped view of one `TemplateVariableDefinition` entry. */
interface ITemplateItem<T = TemplateVariableValues> {
    key: Extract<keyof T, string>;
    label: string;
    description?: string;
}

/** Flattens a `key -> label` definition map into iterable `{ key, label, description? }` items. */
export function toTemplateDefinitionItems(
    definitions: TemplateVariableDefinition,
): ITemplateItem[] {
    return Object.entries(definitions).map(([key, entry]) =>
        typeof entry === "string"
            ? { key, label: entry }
            : { key, label: entry.label, description: entry.description },
    );
}

/**
 * This is the source passed into the template variable extension. It provides type enforcement between the definition and values.
 *
 * This means that the templateDefinition must be of the same generic tpe as the values
 *
 * Values is an optional parameter. If provided, it will be used to resolve the template variable chips to their values. If not provided, the chips will resolve to their labels.
 */
export interface ITemplateVariableSource<
    T extends TemplateVariableValues = TemplateVariableValues,
> {
    /** Namespace of the source. This allows you to have multiple sources in a given editor. For example, you could have a builder source and a job source */
    namespace: string;
    /** Friendly label for the source, to indicate to the user which object the field is from */
    label: string;
    definitions: TemplateVariableDefinition<T>;
    values?: T;
}

/** The attributes persisted on an inserted variable chip node.
 *
 * @remarks - Do not use outside of the editor extension. Meant to be used by internal utilities.
 */
export interface ITemplateVariableAttributes {
    /** The source namespace this chip belongs to; scopes key resolution and prevents collisions. */
    templateNamespace: string;
    /** The stable variable key this chip resolves against, within its namespace. */
    templateKey: string;
    /** Display name shown in the chip while editing. Not used for resolution. */
    templateFriendlyName: string;
}

/** A dropdown entry: a source's definition flattened with its owning source for insertion + grouping.
 *  @remarks - Do not use outside of the editor extension. Meant to be used by internal utilities.
 */
export interface ITemplateVariableSuggestionItem<
    T extends TemplateVariableValues = TemplateVariableValues,
> extends ITemplateItem<T> {
    /** The owning source's namespace, written onto the inserted chip. */
    namespace: string;
    /** The owning source's label, used as the group / submenu header. */
    sourceLabel: string;
    /** The resolved value for this variable, if its source supplies one (shown in the dropdown). */
    value?: string | number;
}

export interface ITemplateVariableOptions {
    sources: ITemplateVariableSource[];
    /** How chips render when the editor is read-only. Defaults to `"replaceVariables"`. */
    readOnlyMode: TemplateVariableReadOnlyMode;
}

/** Finds the source that owns a chip by its stored `source` id. */
function findChipSource(
    attrs: ITemplateVariableAttributes,
    sources: ITemplateVariableSource[],
): ITemplateVariableSource | undefined {
    return sources.find((source) => source.namespace === attrs.templateNamespace);
}

/**
 * Determines which source a chip belongs to.
 *  @remarks - Do not use outside of the editor extension. Meant to be used by internal utilities.
 */
export function resolveTemplateVariableValue(
    attrs: ITemplateVariableAttributes,
    sources: ITemplateVariableSource[],
): string | number | undefined {
    return findChipSource(attrs, sources)?.values?.[attrs.templateKey];
}

/**
 * The display text a chip resolves to, driven by whether the chip's source supplies values at all:
 *  - the source has no `values` object → the variable name in braces (e.g. `{Recipient Name}`), i.e.
 *    a placeholder, because nothing is wired up to resolve this source yet;
 *  - the source has a `values` object but no entry for this key → an empty string, because the data
 *    source is wired up and this field is simply blank (e.g. no job selected yet);
 *  - the source has a value for the key → that value.
 * Shared by the node view (read-only branch) and HTML/text serialization so every render surface
 * resolves identically.
 *
 * @remarks - Do not use outside of the editor extension. Meant to be used by internal utilities.
 */
export function resolveTemplateVariableText(
    attrs: ITemplateVariableAttributes,
    sources: ITemplateVariableSource[],
): string | number {
    const chipSource = findChipSource(attrs, sources);
    // No values object on the source → unresolved placeholder. (An empty object still counts as
    // "wired up", so a missing key below resolves to an empty string, not a placeholder.)
    if (!chipSource?.values) {
        return `{${attrs.templateFriendlyName || attrs.templateKey}}`;
    }
    return chipSource.values[attrs.templateKey] ?? "";
}

/** True when the chip's key is still defined by its source (so it can be flagged if it can't resolve).
 * @remarks - Do not use outside of the editor extension. Meant to be used by internal utilities.
 */
export function isKnownTemplateVariable(
    attrs: ITemplateVariableAttributes,
    sources: ITemplateVariableSource[],
): boolean {
    const chipSource = findChipSource(attrs, sources);
    return chipSource ? attrs.templateKey in chipSource.definitions : false;
}

/**
 * The source label to show as a disambiguating prefix on a chip, or undefined when none is needed.
 * Only returned when the chip's display label is shared by a definition in another source — so chips
 * that would otherwise look identical to the user (e.g. two "Name" chips from different sources) are
 * told apart. Distinct labels (e.g. "Company Name" vs "Recipient Name") need no prefix even when they
 * share a key, since the user can already tell them apart.
 */
export function getDisambiguatingSourceLabel(
    attrs: ITemplateVariableAttributes,
    sources: ITemplateVariableSource[],
): string | undefined {
    const chipSource = findChipSource(attrs, sources);
    const label = chipSource
        ? toTemplateDefinitionItems(chipSource.definitions).find((d) => d.key === attrs.templateKey)
              ?.label
        : undefined;
    if (label === undefined) {
        return undefined;
    }
    // Count how many sources define a variable with this same display label.
    const sourcesWithLabel = sources.filter((source) =>
        toTemplateDefinitionItems(source.definitions).some((d) => d.label === label),
    );
    return sourcesWithLabel.length >= 2 ? chipSource?.label : undefined;
}

/** Flattens every source's definitions into dropdown items carrying their namespace + resolved value. */
function toSuggestionItems(sources: ITemplateVariableSource[]): ITemplateVariableSuggestionItem[] {
    return sources.flatMap((source) =>
        toTemplateDefinitionItems(source.definitions).map((definition) => ({
            ...definition,
            namespace: source.namespace,
            sourceLabel: source.label,
            value: source.values?.[definition.key],
        })),
    );
}

declare module "@tiptap/core" {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- third-party interface augmentation
    interface Commands<ReturnType> {
        templateVariable: {
            /** Insert a template variable chip at the current selection (used by the toolbar button). */
            insertTemplateVariable: (item: ITemplateVariableSuggestionItem) => ReturnType;
        };
    }
}

/**
 * A completed literal like `{lienWaiver:subcontractor}` — used by the paste rule to convert
 * pasted/typed `{namespace:key}` literals into chips. Only matches configured namespace/key pairs; an
 * unknown literal is left as plain text.
 */
const COMPLETED_VARIABLE_REGEX = /\{(\w+):(\w+)\}/g;

const findVariableSuggestionMatch: SuggestionOptions["findSuggestionMatch"] = (config) => {
    const { $position } = config;
    const textBefore = $position.nodeBefore?.isText ? $position.nodeBefore.text : null;
    if (!textBefore) {
        return null;
    }

    const match = /\{([^}]*)$/.exec(textBefore);
    if (!match) {
        return null;
    }

    const from = $position.pos - match[0].length;
    return {
        range: { from, to: $position.pos },
        query: match[1],
        text: match[0],
    };
};

function filterSuggestionItems(
    items: ITemplateVariableSuggestionItem[],
    query: string,
): ITemplateVariableSuggestionItem[] {
    // The query may include a `namespace:` prefix (from a pasted/typed literal). When present, filter
    // the source by the prefix and the variable by the key part; otherwise match the key part alone.
    const normalized = query.trim().toLowerCase();
    const [namespaceQuery, keyQuery] = normalized.includes(":")
        ? [normalized.split(":")[0], normalized.split(":").slice(1).join(":")]
        : ["", normalized];
    if (namespaceQuery.length === 0 && keyQuery.length === 0) {
        return [...items];
    }
    return items.filter(
        (item) =>
            (namespaceQuery.length === 0 ||
                item.namespace.toLowerCase().includes(namespaceQuery) ||
                item.sourceLabel.toLowerCase().includes(namespaceQuery)) &&
            (keyQuery.length === 0 ||
                item.key.toLowerCase().includes(keyQuery) ||
                item.label.toLowerCase().includes(keyQuery)),
    );
}

function buildChipContent(item: ITemplateVariableSuggestionItem) {
    return [
        {
            type: TemplateVariableExtensionName,
            attrs: {
                templateNamespace: item.namespace,
                templateKey: item.key,
                templateFriendlyName: item.label,
            } satisfies ITemplateVariableAttributes,
        },
        { type: "text", text: " " },
    ];
}

/**
 * A rich-text-editor extension that lets users insert "template variable" chips via a `{` trigger
 * with autocomplete. Chips are atom nodes keyed off a stable `source`/`key` pair; a downstream engine
 * resolves each to a real value. Domain-agnostic — the variable sources are passed in via options,
 * and more than one namespaced source can be registered on the same editor.
 */
export const TemplateVariableExtension = Node.create<ITemplateVariableOptions>({
    name: TemplateVariableExtensionName,
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,

    addOptions(): ITemplateVariableOptions {
        return {
            sources: [],
            readOnlyMode: "replaceVariables",
        };
    },

    addAttributes() {
        // A chip is identified by a (templateNamespace, templateKey) pair and carries a
        // templateFriendlyName for display:
        //  - `templateNamespace`: which variable set the chip came from (e.g. "lienWaiver"). Scopes
        //    the key so two sources can each define, say, a "name" variable without colliding.
        //  - `templateKey`: the stable variable id within that namespace (e.g. "subcontractor"). This
        //    is what a downstream engine resolves to a real value; it never changes once inserted.
        //  - `templateFriendlyName`: the human-friendly text shown in the chip while editing (e.g.
        //    "Subcontractor"). Display-only — never used to resolve, so renaming it can't orphan a chip.
        // Each attribute round-trips through a `data-*` HTML attribute so chips survive copy/paste and
        // HTML persistence (see renderHTML / parseHTML below).
        return {
            ["templateNamespace" satisfies keyof ITemplateVariableAttributes]: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-template-namespace") ?? "",
                renderHTML: (attributes: ITemplateVariableAttributes) => ({
                    "data-template-namespace": attributes.templateNamespace,
                }),
            },
            ["templateKey" satisfies keyof ITemplateVariableAttributes]: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-template-key") ?? "",
                renderHTML: (attributes: ITemplateVariableAttributes) => ({
                    "data-template-key": attributes.templateKey,
                }),
            },
            ["templateFriendlyName" satisfies keyof ITemplateVariableAttributes]: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-template-friendly-name") ?? "",
                renderHTML: (attributes: ITemplateVariableAttributes) => ({
                    "data-template-friendly-name": attributes.templateFriendlyName,
                }),
            },
        };
    },

    parseHTML() {
        return [{ tag: `span[data-type=${TemplateVariableExtensionName}]` }];
    },

    renderHTML({ HTMLAttributes, node }) {
        // Serialize the chip as a round-trippable element (matches parseHTML), so copy/paste and any
        // HTML persistence preserve the chip's identity rather than baking in a resolved value. The
        // resolved-value view is produced separately by the read-only node view (see
        // TemplateVariableNodeView + `readOnlyMode`), which replaces chips with text at render time.
        return [
            "span",
            mergeAttributes({ "data-type": this.name }, HTMLAttributes),
            `{${node.attrs.templateFriendlyName}}`,
        ];
    },

    renderText({ node }) {
        return `{${node.attrs.templateFriendlyName}}`;
    },

    addNodeView() {
        return ReactNodeViewRenderer(TemplateVariableNodeView, { as: "span" });
    },

    addCommands() {
        return {
            insertTemplateVariable:
                (item) =>
                ({ chain }) =>
                    chain().focus().insertContent(buildChipContent(item)).run(),
        };
    },

    addPasteRules() {
        return [
            nodePasteRule({
                find: COMPLETED_VARIABLE_REGEX,
                type: this.type,
                getAttributes: (match) => {
                    const [, namespace, key] = match;
                    const source = this.options.sources.find((s) => s.namespace === namespace);
                    const definition = source
                        ? toTemplateDefinitionItems(source.definitions).find((d) => d.key === key)
                        : undefined;
                    if (!source || !definition) {
                        return false;
                    }
                    return {
                        templateNamespace: source.namespace,
                        templateKey: definition.key,
                        templateFriendlyName: definition.label,
                    } satisfies ITemplateVariableAttributes;
                },
            }),
        ];
    },

    addProseMirrorPlugins() {
        const sources = this.options.sources;
        return [
            Suggestion<ITemplateVariableSuggestionItem, ITemplateVariableSuggestionItem>({
                editor: this.editor,
                char: TemplateVariableTriggerChar,
                allowSpaces: true,
                findSuggestionMatch: findVariableSuggestionMatch,

                items: ({ query }) => filterSuggestionItems(toSuggestionItems(sources), query),

                command: ({ editor, range, props }) => {
                    editor.chain().focus().insertContentAt(range, buildChipContent(props)).run();
                },

                render: () => {
                    let popup: ReactRenderer | null = null;

                    const destroyPopup = () => {
                        popup?.destroy();
                        popup = null;
                    };

                    const buildProps = (
                        props: SuggestionProps<ITemplateVariableSuggestionItem>,
                    ): ComponentProps<typeof TemplateVariableSelectOverlay> => ({
                        items: props.items,
                        onSelect: (item) => props.command(item),
                        getReferenceRect: () => props.clientRect?.() ?? null,
                    });

                    return {
                        onStart: (props) => {
                            if (!props.clientRect) {
                                return;
                            }
                            popup = new ReactRenderer(TemplateVariableSelectOverlay, {
                                props: buildProps(props),
                                editor: props.editor,
                            });
                            document.body.appendChild(popup.element);
                        },
                        onUpdate: (props) => {
                            popup?.updateProps(buildProps(props));
                        },
                        onKeyDown: (props) => {
                            if (props.event.key === "Escape") {
                                destroyPopup();
                                return true;
                            }
                            const overlay = popup?.ref as {
                                onKeyDown?: (event: KeyboardEvent) => boolean;
                            } | null;
                            return overlay?.onKeyDown?.(props.event) ?? false;
                        },
                        onExit: () => {
                            destroyPopup();
                        },
                    };
                },
            }),
        ];
    },
});
